import React from 'react';
import {defineMessages} from 'react-intl';
import type {
    DateRangeFilterValue,
    FilterGroupNewFilter,
    FilterProps,
    FiltersProps,
    MultiKeyFilterTextValue,
    QueryFilterAdapterModel,
    QueryFilterAdapterProps,
} from '@igp/shared/components/filter';
import {DateRangeFilter, FilterGroupNew, TextInputWithDropdownFilter, withQueryFilter} from '@igp/shared/components/filter';
import {useLocale} from '@igp/shared/localization';
import type {Locale} from '@igp/shared/style';

import type {RepaymentLogFilterKeys, RepaymentLogFilterModel, RepaymentLogFilterName, RepaymentLogTextFilterKeys} from '../types';

const localized = defineMessages({
    agent_id: {
        id: 'RepaymentLogFilters_agent_id',
        defaultMessage: 'Agent ID',
    },
    master_agent_id: {
        id: 'RepaymentLogFilters_master_agent_id',
        defaultMessage: 'Master Agent ID',
    },
    placeholder: {
        id: 'RepaymentLogFilters_placeholder',
        defaultMessage: 'Search in Repayment Logs',
    },
});

function RepaymentLogFilters(props: Omit<FiltersProps<RepaymentLogFilterModel, RepaymentLogFilterName>, 'submitButton'>) {
    const {locale} = useLocale();

    const filters: FilterGroupNewFilter<RepaymentLogFilterModel, RepaymentLogFilterName>[] = [
        {
            filterName: 'masterAgentUid_agentUid',
            modelField: 'text',
            component: (props: FilterProps<MultiKeyFilterTextValue<RepaymentLogFilterKeys>>) => (
                <TextInputWithDropdownFilter
                    options={[
                        {label: localized.master_agent_id, value: 'master_agent_uid'},
                        {label: localized.agent_id, value: 'from_agent_uid'},
                    ]}
                    inputPlaceholder={localized.placeholder}
                    width="full"
                    {...props}
                />
            ),
            collapseOnMobile: false,
        },
        {
            filterName: 'repaymentDate',
            modelField: 'repaymentDate',
            component: (props: FilterProps<DateRangeFilterValue>) => <DateRangeFilter {...props} width="full" locale={locale as Locale} />,
        },
    ];

    return <FilterGroupNew mode="update-on-change" allFilters={filters} {...props} />;
}

function RepaymentLogFiltersAdapter({
    model,
    onChange,
    ...props
}: Omit<QueryFilterAdapterProps<RepaymentLogFilterKeys, RepaymentLogFilterName, RepaymentLogTextFilterKeys>, 'submitButton'>) {
    const filterViewModel: RepaymentLogFilterModel = {
        text: model?.text,
        repaymentDate: {
            from: model?.['repayment_date.from'] as number,
            to: model?.['repayment_date.to'] as number,
        },
    };

    function handleChange(model: RepaymentLogFilterModel) {
        const result: QueryFilterAdapterModel<RepaymentLogFilterKeys, RepaymentLogTextFilterKeys> = {
            text: model?.text,
            'repayment_date.from': model?.repaymentDate?.from,
            'repayment_date.to': model?.repaymentDate?.to,
        };
        onChange(result);
    }
    return <RepaymentLogFilters model={filterViewModel} onChange={handleChange} {...props} />;
}

export const RepaymentLogQueryFilter = withQueryFilter<RepaymentLogFilterKeys, RepaymentLogFilterName, RepaymentLogTextFilterKeys>(
    RepaymentLogFiltersAdapter,
    {
        masterAgentUid_agentUid: ['master_agent_uid', 'from_agent_uid'],
    }
);
